/**
 * Performance metric reporter. Sinks (console, beacon, in-memory buffer for
 * tests) subscribe via `addPerformanceSink`. `reportPerformance` is a pure
 * dispatcher — no IO of its own — so unit tests can verify call shapes
 * without standing up a browser.
 */

import type { PerfMetric } from "./types.js";

type Sink = (metric: PerfMetric) => void;

const sinks = new Set<Sink>();

export function addPerformanceSink(sink: Sink): () => void {
  sinks.add(sink);
  return () => {
    sinks.delete(sink);
  };
}

export function clearPerformanceSinks(): void {
  sinks.clear();
}

export function reportPerformance(metric: PerfMetric): void {
  // Snapshot so a sink that subscribes/unsubscribes during emit doesn't
  // mutate the iteration order.
  for (const sink of Array.from(sinks)) {
    try {
      sink(metric);
    } catch (err) {
      // One bad sink must not take the rest down. Phase 4 explicitly
      // guards against silent failures — surface this in dev console.
      console.warn("[tree-hole perf] sink threw", err);
    }
  }
}

/** Logs every metric to the console. Use in dev to eyeball values. */
export function consoleSink(metric: PerfMetric): void {
  console.info(
    `[perf] ${metric.name}`,
    metric.value.toFixed ? metric.value.toFixed(2) : metric.value,
    metric.rating ?? "",
  );
}

/**
 * Sends each metric to `endpoint` as JSON. Uses navigator.sendBeacon when
 * available (works on page unload), falls back to fetch with keepalive.
 *
 * The phase-4 plan calls for "上报到自建 endpoint 或简单的 GA 替代" — wire
 * this once an endpoint is provisioned.
 */
export function beaconSink(endpoint: string): Sink {
  return (metric) => {
    if (typeof navigator === "undefined") return;
    const body = JSON.stringify({ ...metric, ts: Date.now() });
    try {
      if (typeof navigator.sendBeacon === "function") {
        const ok = navigator.sendBeacon(endpoint, body);
        if (ok) return;
      }
    } catch {
      // sendBeacon may throw if the URL is invalid; fall through to fetch.
    }
    if (typeof fetch === "function") {
      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {
        // Best effort.
      });
    }
  };
}
